import React, { ReactNode } from 'react';
import { useLocation, matchPath } from 'react-router';
import OrganizationSchema from './OrganizationSchema';
import WebsiteSchema from './WebsiteSchema';
import BreadcrumbListSchema from './BreadcrumbListSchema';
import {
  organizationConfig,
  websiteConfig,
  publicOnlyRoutes,
} from '../../config/seoConfig';

interface BaselineSchemaProviderProps {
  children: ReactNode;
}

function shouldEmitBaselineSchema(pathname: string): boolean {
  return publicOnlyRoutes.some((route) =>
    matchPath({ path: route, end: true }, pathname)
  );
}

/**
 * BaselineSchemaProvider automatically emits baseline JSON-LD schemas for all public routes.
 * Emits:
 * - Organization schema (once per page)
 * - Website schema with SearchAction
 * - BreadcrumbList schema (route-aware)
 *
 * This provider eliminates the need to manually add schemas to each page.
 * Place it once in the public layout or at the root of public routes.
 *
 * @param children React components to wrap
 */
export function BaselineSchemaProvider({
  children,
}: BaselineSchemaProviderProps) {
  const location = useLocation();
  const shouldEmitSchemas = shouldEmitBaselineSchema(location.pathname);

  return (
    <>
      {shouldEmitSchemas && (
        <>
          <OrganizationSchema organization={organizationConfig} />
          <WebsiteSchema
            url={websiteConfig.url}
            name={websiteConfig.name}
            description={websiteConfig.description}
            searchUrl={websiteConfig.searchUrl}
          />
          <BreadcrumbListSchema pathname={location.pathname} />
        </>
      )}

      {children}
    </>
  );
}
